import React from "react";
import get from "lodash/get";
import find from "lodash/find";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation } from "react-router-dom";
import { useLocalStorage, useUpdateEffect } from "react-use";
import { useStoreActions, useStoreState } from "app/state/store/hooks";
import { useCMSData } from "./useCMSData";
import { getHeaderButtonItems } from "app/components/header/sub-components/buttons/data";

export interface IFavourite {
  id: string;
  title: string;
  itemId: string;
  status: string;
  commitment: number;
  locations: string[];
  isFutureActivity: boolean;
  commitmentPercentage: number;
  type: "page" | "widget" | "activity";
}

export interface IFavouriteAlert {
  id: string;
  bookmarkId: string;
}

export const useFavouritesUtils = () => {
  const location = useLocation();

  const setActiveHeaderButton = useStoreActions(
    (state) => state.activeHeaderButton.setActiveButton
  );

  const setLoginFlowData = useStoreActions(
    (state) => state.activeHeaderButton.setLoginFlow
  );

  const cmsData = useCMSData({ returnData: true });

  const HEADER_BUTTON_ITEMS = React.useMemo(() => {
    return getHeaderButtonItems(cmsData);
  }, [cmsData]);

  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();

  const setAuthRedirectData = useLocalStorage("auth-redirect-data", {
    step: -1,
    pathname: "/",
  })[1];

  const [token, setToken] = React.useState<string | null>(null);

  const [
    favouritedActivityId,
    setFavouritedActivityId,
    clearFavouritedActivityId,
  ] = useLocalStorage("favourited-activity-id", "");

  const count = useStoreState((state) =>
    get(state.GetBookmarks, "data.count", 0)
  );
  const favourites = useStoreState(
    (state) => get(state.GetBookmarks, "data.data", []) as IFavourite[]
  );
  const alerts = useStoreState(
    (state) => get(state.GetAlerts, "data.data", []) as IFavouriteAlert[]
  );
  const fetchFavouritesAction = useStoreActions(
    (actions) => actions.GetBookmarks.authGetFetch
  );
  const fetchAlertsAction = useStoreActions(
    (actions) => actions.GetAlerts.authGetFetch
  );
  const addFavouriteResponse = useStoreState(
    (state) => state.CreateBookmark.data
  );
  const addAlertResponse = useStoreState((state) => state.CreateAlert.data);
  const addFavouriteAction = useStoreActions(
    (actions) => actions.CreateBookmark.authPostFetch
  );
  const addAlertAction = useStoreActions(
    (actions) => actions.CreateAlert.authPostFetch
  );
  const removeFavouriteResponse = useStoreState(
    (state) => state.DeleteBookmark.data
  );
  const removeAlertResponse = useStoreState((state) => state.DeleteAlert.data);
  const removeFavouriteAction = useStoreActions(
    (actions) => actions.DeleteBookmark.authDeleteFetch
  );
  const removeAlertAction = useStoreActions(
    (actions) => actions.DeleteAlert.authDeleteFetch
  );
  const editFavouriteAction = useStoreActions(
    (actions) => actions.UpdateBookmark.authPatchFetch
  );
  const editFavouriteResponse = useStoreState(
    (state) => state.UpdateBookmark.data
  );
  const loading = useStoreState(
    (state) =>
      state.GetBookmarks.loading ||
      state.CreateBookmark.loading ||
      state.DeleteBookmark.loading
  );

  const getAccessTokenSilentlyWithState = async () => {
    if (isAuthenticated && !token) {
      const newToken = await getAccessTokenSilently();
      setToken(newToken);
      return newToken;
    }
    return token;
  };

  const fetchFavourites = async () => {
    if (!isAuthenticated || !user?.sub) return;
    const token = await getAccessTokenSilentlyWithState();
    fetchFavouritesAction({
      dontEncryptAuthCall: true,
      values: { token },
      filterString: `filter={"where":{"userId":"${user?.sub}"}}`,
    });
  };

  const fetchAlerts = async () => {
    if (!isAuthenticated || !user?.sub) return;
    const token = await getAccessTokenSilentlyWithState();
    fetchAlertsAction({
      dontEncryptAuthCall: true,
      values: { token },
      filterString: `filter={"where":{"userId":"${user?.sub}"}}`,
    });
  };

  const checkIfAuthenticated = (values: {
    name: string;
    path: string;
    itemId: string;
    type?: string;
  }) => {
    if (!isAuthenticated) {
      setAuthRedirectData({
        step: 1,
        pathname: location.pathname,
      });
      setActiveHeaderButton(HEADER_BUTTON_ITEMS[4].key);
      setLoginFlowData({
        showTooltip: true,
        tooltipTitle: "Sign in to favourite data assets!",
        tooltipText: `To save your favorite data assets and access your favorites list, simply create an account with one click.\n \n Favourited data assets would appear on My Favourites page.`,
        metadata: {
          favouriteActivityId: values.itemId,
          favouriteActivityName: values.name,
          favouriteActivityPath: values.path,
          favouriteActivityType: values.type ?? "activity",
        },
      });
      return false;
    }
    return true;
  };

  const addFavourite = async (values: {
    name: string;
    path: string;
    itemId: string;
    type?: string;
  }) => {
    if (!checkIfAuthenticated(values)) return;

    const token = await getAccessTokenSilentlyWithState();
    await addFavouriteAction({
      dontEncryptAuthCall: true,
      values: { ...values, userId: user?.sub, token },
    });
    setFavouritedActivityId(values.itemId);
  };

  const addAlert = async (values: { favouriteId: string }) => {
    if (!isAuthenticated) return;
    const token = await getAccessTokenSilentlyWithState();
    await addAlertAction({
      dontEncryptAuthCall: true,
      values: {
        token,
        userId: user?.sub,
        email: user?.email,
        bookmarkId: values.favouriteId,
      },
    });
  };

  const removeFavourite = async (id: string) => {
    if (!isAuthenticated) return;
    const token = await getAccessTokenSilentlyWithState();
    removeFavouriteAction({
      dontEncryptAuthCall: true,
      values: { token },
      routeParams: { id },
    });
  };

  const removeAlert = async (id: string) => {
    if (!isAuthenticated) return;
    const token = await getAccessTokenSilentlyWithState();
    removeAlertAction({
      dontEncryptAuthCall: true,
      values: { token },
      routeParams: { id },
    });
  };

  const onFavouriteButtonClick = (values: {
    name: string;
    path: string;
    itemId: string;
    type: string;
  }) => {
    const isFavourite = find(favourites, {
      itemId: values.itemId,
      type: values.type,
    }) as IFavourite | undefined;

    if (isFavourite) {
      removeFavourite(isFavourite.id);
    } else {
      addFavourite(values);
    }
  };

  const onAlertButtonClick = (favouriteId: string) => {
    const isAlerted = find(alerts, {
      bookmarkId: favouriteId,
    }) as IFavouriteAlert;
    if (isAlerted) {
      removeAlert(isAlerted.id);
    } else {
      addAlert({ favouriteId });
    }
  };

  const onEditFavourite = async (values: { id: string; name: string }) => {
    if (!isAuthenticated) return;
    const token = await getAccessTokenSilentlyWithState();
    await editFavouriteAction({
      dontEncryptAuthCall: true,
      routeParams: { id: values.id },
      values: { name: values.name, token },
    });
  };

  useUpdateEffect(() => {
    if (
      !addFavouriteResponse &&
      !removeFavouriteResponse &&
      !editFavouriteResponse
    )
      return;
    fetchFavourites();
  }, [addFavouriteResponse, removeFavouriteResponse, editFavouriteResponse]);

  useUpdateEffect(() => {
    if (!addAlertResponse && !removeAlertResponse) return;
    fetchAlerts();
  }, [addAlertResponse, removeAlertResponse]);

  return {
    count,
    alerts,
    loading,
    favourites,
    addFavourite,
    fetchFavourites,
    removeFavourite,
    onEditFavourite,
    onAlertButtonClick,
    favouritedActivityId,
    onFavouriteButtonClick,
    setFavouritedActivityId,
    clearFavouritedActivityId,
  };
};
